import {
  Box,
  Collapse,
  Text,
  VStack,
  HStack,
  Checkbox,
  Select,
} from '@chakra-ui/react';

import SelectSearch from 'components/fields/SelectSearch';
import { useAnswerCodes } from '../../hooks';
import { ValidatedField } from '..';
import type { CoverageSectionProps } from './types';

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear - 1899 }, (_, i) => ({
  label: String(currentYear - i),
  value: String(currentYear - i),
}));

const POOL_TYPE_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'above_ground', label: 'Above Ground' },
  { value: 'inground', label: 'Inground' },
  { value: 'indoor', label: 'Indoor' },
];

const SwimmingPoolSection = ({
  coverages,
  updateCoverage,
  isLoadingCodes,
}: CoverageSectionProps) => {
  const { getOptions } = useAnswerCodes();

  const handleFencedChange = (value: string) => {
    if (value === '') {
      updateCoverage('swimming_pool_fenced', null);
    } else {
      updateCoverage('swimming_pool_fenced', value === 'true');
    }
  };

  const getFencedValue = (): string => {
    if (coverages.swimming_pool_fenced === null) return '';
    return coverages.swimming_pool_fenced ? 'true' : 'false';
  };

  return (
    <Box>
      <HStack mb={3}>
        <Text fontWeight="700">Swimming Pool</Text>
        <Checkbox
          isChecked={coverages.swimming_pool_enabled}
          onChange={(e) =>
            updateCoverage('swimming_pool_enabled', e.target.checked)
          }
          colorScheme="blue"
        />
      </HStack>
      <Collapse in={coverages.swimming_pool_enabled} animateOpacity>
        <VStack align="stretch" spacing={3}>
          <HStack spacing={4} align="flex-end">
            <ValidatedField
              itemPath="location"
              fieldPath="coverages.swimming_pool_type"
              label="Pool Type"
              formControlProps={{ flex: 1 }}
            >
              <Select
                variant="main"
                value={coverages.swimming_pool_type || ''}
                onChange={(e) =>
                  updateCoverage('swimming_pool_type', e.target.value)
                }
              >
                {POOL_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </ValidatedField>
            <ValidatedField
              itemPath="location"
              fieldPath="coverages.swimming_pool_material"
              label="Material"
              formControlProps={{ flex: 1 }}
            >
              <Select
                variant="main"
                value={coverages.swimming_pool_material || ''}
                onChange={(e) =>
                  updateCoverage('swimming_pool_material', e.target.value)
                }
                isDisabled={isLoadingCodes}
              >
                {getOptions('swimming_pool_material').map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </ValidatedField>
          </HStack>
          <HStack spacing={4} align="flex-end">
            <ValidatedField
              itemPath="location"
              fieldPath="coverages.swimming_pool_year_built"
              label="Year Built"
              formControlProps={{ flex: 1 }}
            >
              <SelectSearch
                options={YEAR_OPTIONS}
                value={coverages.swimming_pool_year_built || ''}
                onChange={(val) =>
                  updateCoverage(
                    'swimming_pool_year_built',
                    val ? String(val) : null,
                  )
                }
                placeholder="Select year..."
                allowClear
              />
            </ValidatedField>
            <ValidatedField
              itemPath="location"
              fieldPath="coverages.swimming_pool_fenced"
              label="Fenced?"
              formControlProps={{ flex: 1 }}
            >
              <Select
                variant="main"
                value={getFencedValue()}
                onChange={(e) => handleFencedChange(e.target.value)}
              >
                <option value="">Select...</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </Select>
            </ValidatedField>
          </HStack>
        </VStack>
      </Collapse>
    </Box>
  );
};

export default SwimmingPoolSection;
